import { useQuery } from "@tanstack/react-query";
import { getCoreAxiosInstance } from "../../api/CoreAxiosInstance";
import { CLIENT_PAY_NORMALISATION_API_PATHS } from "../config";
import type { ClientPayImportSource } from "../types/importSource";
import { ImportSourceListResultSchema } from "../types/clientPayNormalisationApi";
import { toClientPayNormalisationError } from "../utils/clientPayNormalisationApiError";
import { normalizeCoreApiJson } from "../utils/normalizeCoreApiJson";

export const clientPayImportSourcesQueryKey = ["clientPayImportSources"] as const;

/** Import sources rarely change; still refetch on focus so a 503 on first load can recover. */
export const CLIENT_PAY_IMPORT_SOURCES_STALE_MS = 5 * 60 * 1000;

async function fetchClientPayImportSources(): Promise<ClientPayImportSource[]> {
  const instance = await getCoreAxiosInstance();
  try {
    const { data } = await instance.get(
      CLIENT_PAY_NORMALISATION_API_PATHS.importSources,
    );
    const parsed = ImportSourceListResultSchema.parse(normalizeCoreApiJson(data));
    if (parsed.importSources.length === 0) {
      throw new Error(
        "No import sources are available. The normalisation API may be disabled or misconfigured.",
      );
    }
    return parsed.importSources;
  } catch (error) {
    throw toClientPayNormalisationError(error);
  }
}

export function useClientPayImportSources() {
  return useQuery({
    queryKey: clientPayImportSourcesQueryKey,
    queryFn: fetchClientPayImportSources,
    staleTime: CLIENT_PAY_IMPORT_SOURCES_STALE_MS,
    refetchOnWindowFocus: true,
    retry: false,
  });
}
